import * as React from 'react';
import { toastr } from 'react-redux-toastr';
import { Row, Col, Button } from 'reactstrap';
import * as ReactGA from 'react-ga';
import { useHistory } from 'react-router';
import { BeatLoader } from 'react-spinners';
import { useSelector } from 'react-redux';
import { match } from 'react-router-dom';
import { IReduxState } from 'src/reduxTypes';
import { useAuth } from 'src/modules/Auth/hooks/useAuth';
import { initialManager, IManager, ManagerUserTypes } from 'src/share/types/manager';
import { ManagersService } from 'src/share/services/managers';
import SidebarContainer from 'src/modules/Routing/Sidebar/SidebarContainer';
import Routes from 'src/modules/Routing/routes';
import { UserTypes } from 'src/modules/Auth/store/types';
import '../managersView.css';
import { getUserTypeByAgencyCode } from 'src/modules/UserSettings/Users/components/UsersTable';
import ConfirmationModal from 'src/share/components/ConfirmationModal/ConfirmationModal';
import { RoutesModules } from 'src/modules/Routing/types';

export interface IManagerInfoProps {
  match: match<{ managerId: string }>;
};

const ManagerInfo = (props: IManagerInfoProps) => {
  const [auth] = useAuth();
  const history = useHistory();
  const agencies = useSelector((state: IReduxState) => state.auth.agencies);
  const [managerInfo, setManagerInfo] = React.useState<IManager>(initialManager);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [isAdminView, setIsAdminView] = React.useState<boolean>(false);
  const [showDelete, setShowDelete] = React.useState<boolean>(false);
  const formUser = isAdminView ? 'User' : 'Manager';

  const checkIfIsAdminView = () => {
    const path = history.location.pathname;
    return path.includes('admin/');
  }

  const loadManager = async () => {
    setLoading(true);
    const { managerId } = props.match.params;
    try {
      const res = await ManagersService.getByID(managerId);
      if (!res) {
        throw { code: 1 }
      }
      setManagerInfo(res);
      const adminView = checkIfIsAdminView();
      setIsAdminView(adminView);
      setLoading(false);
    }
    catch (error) {
      toastr.error('Error', `There was a problem loading the ${formUser} data`);
      setLoading(false);
    }
  }

  React.useEffect(() => {
    loadManager();
  }, []);

  const onEdit = () => {
    const { managerId } = managerInfo;
    const route = isAdminView ? Routes.ADMINUSERSEDIT : Routes.EDITMANAGER;
    history.push(route.replace(':managerId', managerId))
  }

  const deleteManager = async () => {
    try {
      setLoading(true);
      await ManagersService.delete(managerInfo.managerId);
      toastr.success(`Delete ${formUser}`, `The ${formUser.toLocaleLowerCase()} has been deleted`);
      setLoading(false);
      ReactGA.event({
        category: 'Manager',
        action: 'Deleted manager',
        label: 'Managers management'
      });
      const route = isAdminView ? Routes.ADMINUSERS : Routes.MANAGERSVIEW;
      history.push(route);
    } catch (error) {
      toastr.error('Error', `There was a problem deleting the ${formUser}`);
      setLoading(false);
    }
  }

  const getAgencyName = () => {
    const userType = getUserTypeByAgencyCode(managerInfo.agencyCode);
    const agency = agencies.filter(ag => (ag.code === managerInfo.agencyCode))[0];
    const agencyName = agency ? agency.name : ManagerUserTypes[userType];
    return agencyName;
  }

  const toggleDeleteModal = () => {
    setShowDelete(prev => !prev);
  }

  return (
    <>
      {!loading && managerInfo ?
        <div className="manager-info-container white-background">
          <Row>
            <Col md="12">
              <div className="manager-name">
                <h1>{managerInfo.name}</h1>
              </div>
              <div className="manager-data">
                <p><label>Agency:</label><span className="subFont">{getAgencyName()}</span></p>
                <p><label>User Type:</label><span className="subFont">{getUserTypeByAgencyCode(managerInfo.agencyCode)}</span></p>
                <p><label>Phone:</label><span className="subFont">{managerInfo.phone ? managerInfo.phone : 'Unknown'}</span></p>
                <p><label>Email:</label><a href={`mailto:${managerInfo.email}`}><span className="subFont">{managerInfo.email}</span></a></p>
                <p><label>Address:</label><span className="subFont">{managerInfo.address ? managerInfo.address : 'Unknown'}</span></p>
              </div>
              {auth.userType === UserTypes.ADMIN &&
                <div className="controls">
                  <Button type="submit" id="submitBTN" className="btn btn-outline float-right btn-blue" onClick={onEdit}>
                    Edit
                </Button>
                  <Button color="link" className="float-right btn-delete" onClick={toggleDeleteModal}>
                    {`Delete ${formUser}`}
                  </Button>
                </div>}
            </Col>
          </Row>
        </div>
        : null
      }
      <ConfirmationModal
        isOpen={showDelete}
        toggle={() => {
          toggleDeleteModal();
        }}
        title={loading ? `Deleting ${formUser}` : `Delete ${formUser}`}
        description={loading ? `The ${formUser.toLowerCase()} will be deleted from the system` : `Are you sure you want to delete this ${formUser}?`}
        onContinue={deleteManager}
        loading={loading}
        showButtons={!loading}
      />
      <div className="help-loader">
        <BeatLoader loading={loading} />
      </div>
    </>
  )
}

export const AgenciesManagerInfo = SidebarContainer(RoutesModules.AGENCIES)(ManagerInfo);
export const AdminManagerInfo = SidebarContainer(RoutesModules.ADMIN)(ManagerInfo);



// WEBPACK FOOTER //
// ./src/modules/Agencies/Manager/View/components/ManagerInfo.tsx